import classNames from 'classnames';
import React from 'react';
import SVGIcon from '../../components/SVGIcon';
import PricingModalButton from '../../organization/PricingModalButton';
import { isFeatureEnabled } from '../../utils/features';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import Link from '../../utils/router/Link';
import { iframeIcon } from '../controls/editorIcons';
import * as styles from './IFrameComponent.module.scss';

function isSameOrigin(url: string): boolean {
  const a = document.createElement('a');
  a.href = url;

  return (
    a.hostname === window.location.hostname &&
    a.port === window.location.port &&
    a.protocol === window.location.protocol
  );
}

export function isIFrameSrcAllowed(url: string): boolean {
  // Disallow embedding same-origin URLs because Slab
  // pages don't work without "allow-same-origin".
  return !isSameOrigin(url);
}

interface IFrameComponentProps {
  src: string;
}

const IFrameComponent = ({ src }: IFrameComponentProps) => {
  const sandbox = ['allow-popups', 'allow-scripts', 'allow-forms'];
  if (!isSameOrigin(src)) {
    // It's insecure to apply "allow-same-origin" when the src
    // has the same origin because the embeded page can escape
    // sandbox.
    // https://html.spec.whatwg.org/multipage/iframe-embed-object.html#:~:text=setting%20both%20the%20allow-scripts%20and%20allow-same-origin%20keywords
    sandbox.push('allow-same-origin');
  }

  const isEditorIframeEnabled = isFeatureEnabled('editor_iframe');

  return (
    <div className={classNames(styles.container, 'iframe-content')}>
      {isEditorIframeEnabled ? (
        <iframe
          {...ignoreInChromatic()}
          src={src}
          title="Embed"
          sandbox={sandbox.join(' ')}
        />
      ) : (
        <div className={styles.upsell}>
          <div className={styles.upgrade}>
            <div className={styles.upgradeTitle}>Upgrade for iframe embed</div>
            <div className={styles.upgradeDescription}>
              Upgrade to allow any source to be embedded into Slab, not just
              available integrations.
            </div>
            <PricingModalButton feature="customEmbedHosts">
              View upgrade options
            </PricingModalButton>
          </div>
        </div>
      )}
      <Link className={styles.footer} to={src} newTab="ugc">
        <div className={styles.logo}>
          <SVGIcon size={16}>{iframeIcon}</SVGIcon>
        </div>
        <div className={styles.link}>{src}</div>
      </Link>
    </div>
  );
};

export default IFrameComponent;
